import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import courseImg from '../../assets/images/course/course1.png';
import StarRatingDisplay from '../common/StarRatingDisplay';
import AppTooltip from '../ui/AppTooltip';


const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'SGD':
      return 'S$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$';
  }
};

function CourseCatalogCard({
  course,
  onPrimaryAction,
  showRecommendation = false,
  showRecommendationPill = false,
  recommendationText,
  isPrimaryLoading = false,
  primaryLabel,
  className = '',
  compact = false,
  showPrice = true,
  isEnrolled = false,
  isCompleted = false,
  progress = null,
  progressLoading = false,
  disablePrimaryAction = false,
  reserveProgressSpace = false,
  onClassroomAction,
}) {
  const [isOpening, setIsOpening] = useState(false);
  const openTimer = useRef(null);
  const isFree = course.course_type?.toLowerCase() === 'free' || course.price === 'Free';
  const computedPrimaryLabel = primaryLabel || (isEnrolled ? 'Continue' : (isFree ? 'Watch Now' : 'Enroll Now'));
  const isContinueAction = computedPrimaryLabel.toLowerCase().includes('continue');
  const isLoading = isOpening || isPrimaryLoading;
  const displayProgress = isCompleted ? 100 : progress;
  const hasProgress = typeof displayProgress === 'number' && Number.isFinite(displayProgress);

  useEffect(() => () => window.clearTimeout(openTimer.current), []);

  const openCourse = () => {
    if (!onPrimaryAction || isLoading || disablePrimaryAction) return;
    onPrimaryAction(course);
  };

  const handlePrimaryAction = () => {
    if (!onPrimaryAction || isLoading || disablePrimaryAction) return;

    setIsOpening(true);
    openTimer.current = window.setTimeout(() => {
      setIsOpening(false);
      onPrimaryAction(course);
    }, 500);
  };

  return (
    <div
      className={`[&_.ui-card-img-top]:tw-rounded-[12px_12px_0_0] tw-relative tw-h-full tw-rounded-[var(--radius-lg)] tw-overflow-hidden [transition:transform_0.3s_ease] tw-bg-[var(--bg-primary)] tw-border-[1px] tw-border-solid tw-border-[var(--border-color)] tw-shadow-[var(--shadow-sm)] [&:hover]:[transform:translateY(-5px)] [&:hover]:tw-shadow-[0_10px_20px_rgba(0,_0,_0,_0.1)] course-card   ${showRecommendation ? "tw-border-[#dbe7ff] tw-shadow-[0_12px_28px_rgba(49,_82,_232,_0.08)] recommended-course-card" : ""}      ${
        compact ? "tw-flex tw-flex-col [&_.course-image]:tw-pt-[50%] [&_.course-details]:tw-flex [&_.course-details]:[flex:1] [&_.course-details]:tw-flex-col [&_.course-details]:tw-p-3 [&_.course-title]:tw-min-h-[2.8em] [&_.course-description--clamped]:[-webkit-line-clamp:2] [&_.course-description--clamped]:tw-h-[3em] [&_.course-description--clamped]:tw-mb-[0.55rem] [&_.course-meta-info]:tw-mt-0 [&_.course-meta-info]:tw-mb-[0.55rem] [&_.meta-row]:tw-mb-[0.35rem] [&_.course-footer]:tw-mt-auto [&_.course-footer]:tw-pt-[0.55rem] [&_.watch-now-btn]:tw-p-[0.35rem_0.65rem] [&_.watch-now-btn]:tw-text-[0.8rem] [&_.watch-now-btn]:tw-gap-1 [&_.watch-now-btn]:tw-min-w-[0] [&_.watch-now-btn_.btn-icon]:tw-text-[1rem] max-[768px]:[&_.course-details]:tw-p-[0.65rem] max-[768px]:[&_.course-image]:tw-pt-[44%] max-[768px]:[&_.course-title]:tw-text-[0.9rem] max-[768px]:[&_.course-title]:tw-mb-[0.35rem] max-[768px]:[&_.course-description]:tw-text-[0.8rem] max-[768px]:[&_.course-description]:tw-mb-2 max-[768px]:[&_.course-description--clamped]:[-webkit-line-clamp:3] max-[768px]:[&_.course-description--clamped]:tw-h-[4.5em] max-[768px]:[&_.meta-row]:tw-mb-[0.35rem] max-[768px]:[&_.meta-row]:tw-text-[0.8rem] max-[768px]:[&_.course-footer]:tw-pt-2 max-[768px]:[&_.watch-now-btn]:tw-min-w-[0] max-[768px]:[&_.watch-now-btn]:tw-text-[0.78rem] max-[768px]:[&_.watch-now-btn]:tw-p-[0.32rem_0.55rem] course-card--compact" : ""
      }      ${onPrimaryAction && !disablePrimaryAction ? "tw-cursor-pointer [&:not([aria-busy='true']):hover]:[transform:translateY(-2px)] [&:not([aria-busy='true']):hover]:tw-shadow-[var(--shadow-md,_0_8px_18px_rgba(15,_23,_42,_0.12))] course-card--clickable" : ""}      ${className}`.trim()}
      onClick={openCourse}
      aria-busy={isLoading}
      style={{ cursor: disablePrimaryAction ? 'default' : undefined }}
    >
      <div className={"tw-relative tw-overflow-hidden tw-pt-[56.25%] [&_img]:tw-absolute [&_img]:tw-top-0 [&_img]:tw-left-0 [&_img]:tw-w-full [&_img]:tw-h-full [&_img]:tw-object-cover [&_img]:[transition:transform_0.3s_ease] course-image"}>
        <img
          src={course.image || courseImg}
          alt={course.title}
          className="tw-max-w-full"
          onError={(e) => {
            e.currentTarget.src = courseImg;
          }}
        />
        {showRecommendationPill && (
          <span className="tw-absolute tw-top-[12px] tw-left-[12px] tw-inline-flex tw-items-center tw-p-[0.35rem_0.75rem] tw-rounded-[999px] [background:rgba(15,_23,_42,_0.78)] tw-text-[#fff] tw-text-[0.75rem] tw-font-semibold tw-z-[1] recommendation-pill">
            <Icon icon="mdi:star-four-points-circle-outline" className="tw-me-1" />
            Recommended
          </span>
        )}
        {course.access_scope === 'classroom_only' ? (
          <AppTooltip content="Available only through your linked classroom membership" side="bottom" align="end">
            <span className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-border tw-border-indigo-200 tw-bg-indigo-50/95 tw-px-3 tw-py-1 tw-text-[0.7rem] tw-font-bold tw-uppercase tw-tracking-wide tw-text-indigo-700 tw-shadow-sm">
              <Icon icon="fluent:building-people-24-regular" /> Classroom
            </span>
          </AppTooltip>
        ) : course.is_public && (
          <AppTooltip content={course.organization_name ? `By ${course.organization_name}` : 'Public course, open to everyone'} side="bottom" align="end">
            <button
              type="button"
              className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-inline-flex tw-max-w-[72%] tw-items-center tw-rounded-full tw-border tw-border-blue-200 tw-bg-blue-50/95 tw-px-3 tw-py-1 tw-text-[0.7rem] tw-font-bold tw-uppercase tw-tracking-wide tw-text-blue-700 tw-shadow-sm focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-brand-600"
              aria-label={course.organization_name ? `Public course from ${course.organization_name}` : 'Public course'}
              onClick={(event) => event.stopPropagation()}
            >
              Public
            </button>
          </AppTooltip>
        )}
        {course.access_scope === 'classroom_only' && course.has_current_access !== false && course.classrooms?.length > 0 && (
          <div className="tw-absolute tw-bottom-3 tw-left-3 tw-z-10 tw-flex tw-max-w-[calc(100%_-_1.5rem)] tw-items-center tw-gap-1.5">
            {onClassroomAction ? (
              <button
                type="button"
                className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-white/70 tw-bg-white/95 tw-px-2.5 tw-py-1.5 tw-text-left tw-text-xs tw-font-semibold tw-text-indigo-700 tw-shadow-md tw-backdrop-blur-sm hover:tw-bg-white focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-indigo-600"
                onClick={(event) => { event.stopPropagation(); onClassroomAction(course.classrooms[0]); }}
                title={course.classrooms[0]?.name}
                aria-label={`Open classroom ${course.classrooms[0]?.name}`}
              >
                <Icon icon="fluent:building-people-24-regular" className="tw-shrink-0" />
                <span className="tw-truncate">{course.classrooms[0]?.name}</span>
              </button>
            ) : (
              <span className="tw-inline-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-border-white/70 tw-bg-white/95 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-indigo-700 tw-shadow-md tw-backdrop-blur-sm" title={course.classrooms[0]?.name}>
                <Icon icon="fluent:building-people-24-regular" className="tw-shrink-0" />
                <span className="tw-truncate">{course.classrooms[0]?.name}</span>
              </span>
            )}
            {course.classrooms.length > 1 && (
              <AppTooltip content={course.classrooms.slice(1).map((classroom) => classroom.name).join(', ')}>
                <span className="tw-shrink-0 tw-rounded-full tw-border tw-border-white/70 tw-bg-white/95 tw-px-2 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-700 tw-shadow-md">+{course.classrooms.length - 1}</span>
              </AppTooltip>
            )}
          </div>
        )}
      </div>

      <div className="tw-p-[var(--spacing-sm)] max-[768px]:tw-p-[var(--spacing-md)] course-details">
        <h5 className="[font-weight:var(--font-weight-semibold)] tw-text-[var(--text-primary)] tw-mb-[var(--spacing-sm)] [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] tw-overflow-hidden tw-text-[0.95rem] tw-leading-[1.4] tw-min-h-[2.66rem] course-title">{course.title}</h5>
        <p className="tw-text-[var(--text-secondary)] tw-text-[0.875rem] tw-mb-3 [display:-webkit-box] [-webkit-box-orient:vertical] tw-overflow-hidden tw-leading-[1.5] tw-opacity-[0.8] [-webkit-line-clamp:4] tw-h-[6em] course-description course-description--clamped">{course.description}</p>

        {course.access_scope === 'classroom_only' && (course.has_current_access === false || !course.classrooms?.length) && (
          <div className="tw-mb-3 tw-flex tw-min-h-7 tw-items-center tw-gap-2 tw-text-xs">
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-md tw-bg-rose-50 tw-px-2 tw-py-1 tw-font-medium tw-text-rose-700">
              <Icon icon="fluent:lock-closed-24-regular" /> Classroom access removed
            </span>
          </div>
        )}

        {showRecommendation && recommendationText && (
          <div className="tw-flex tw-items-start tw-mb-3 tw-p-[0.65rem_0.75rem] tw-rounded-[12px] tw-bg-[#f7f4ea] tw-text-[#6b5b00] tw-text-[0.83rem] tw-leading-[1.45] recommendation-reason">
            <Icon icon="mdi:lightbulb-on-outline" className="tw-me-1" />
            {recommendationText}
          </div>
        )}

        <div className="tw-mb-3 course-meta-info">
          <div className={"tw-mb-2 tw-text-[0.875rem] tw-text-[var(--text-secondary)] [&:last-child]:tw-mb-0 meta-row tw-flex tw-justify-between"}>
            <div className="enrolled-count" aria-label={`${course.enrolled || 0} enrolled`}>
              <Icon icon="mdi:account-group-outline" className="tw-mr-1 tw-align-middle meta-icon" aria-hidden="true" />
              <span>{course.enrolled}</span>
            </div>
            <StarRatingDisplay rating={course.rating} className="tw-inline-flex tw-items-center tw-gap-[4px] rating-stars-container" />
          </div>

          <div className={"tw-mb-2 tw-text-[0.875rem] tw-text-[var(--text-secondary)] [&:last-child]:tw-mb-0 meta-row tw-flex tw-justify-between"}>
            <div className="course-duration">
              <Icon icon="mdi:clock-outline" className="tw-mr-1 tw-align-middle meta-icon" />
              <span>
                {course.modules} module{course.modules !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="course-duration">
              <Icon icon="mdi:signal-cellular-outline" className="tw-mr-1 tw-align-middle meta-icon" />
              <span>{course.level}</span>
            </div>
          </div>

          {course.category && (
            <div className={"tw-mb-2 tw-text-[0.875rem] tw-text-[var(--text-secondary)] [&:last-child]:tw-mb-0 meta-row tw-flex tw-justify-between tw-items-center"}>
              <div className={"tw-flex tw-items-center tw-gap-[0.35rem] tw-w-full tw-min-w-[0] tw-leading-[1.5] tw-overflow-hidden [&_span]:tw-block [&_span]:[flex:1_1_auto] [&_span]:tw-min-w-[0] [&_span]:tw-overflow-hidden [&_span]:tw-text-ellipsis [&_span]:tw-whitespace-nowrap course-duration course-category"}>
                <Icon icon="mdi:shape-outline" className="tw-align-middle [flex:0_0_auto] tw-m-[0.2em_0_0] meta-icon course-category__icon" />
                <AppTooltip content={course.category}>
                  <button type="button" className="tw-min-w-0 tw-flex-1 tw-truncate tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-inherit focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-brand-600" onClick={(event) => event.stopPropagation()} aria-label={`Category: ${course.category}`}>
                    {course.category}
                  </button>
                </AppTooltip>
              </div>
            </div>
          )}
        </div>

        <div className="tw-flex tw-justify-between tw-items-center tw-border-t-[1px] tw-border-solid tw-border-t-[var(--border-color)] tw-pt-3 tw-mt-auto tw-gap-2 [&_.watch-now-btn:disabled[aria-busy='true']]:!tw-bg-[#4054d6] [&_.watch-now-btn:disabled[aria-busy='true']]:!tw-text-[#fff] [&_.watch-now-btn:disabled[aria-busy='true']]:!tw-opacity-[.82] [&_.watch-now-btn:disabled[aria-busy='true']:hover]:!tw-bg-[#4054d6] [&_.watch-now-btn:disabled[aria-busy='true']:hover]:!tw-text-[#fff] [&_.watch-now-btn:disabled[aria-busy='true']:hover]:!tw-opacity-[.82] [&_.watch-now-btn:disabled[aria-busy='true']_.ui-spinner]:tw-border-[rgba(255,_255_255_/_0.55)] [&_.watch-now-btn:disabled[aria-busy='true']_.ui-spinner]:tw-border-r-[transparent] [&_.watch-now-btn:disabled[aria-busy='true']_.ui-spinner]:tw-border-t-[#fff] course-footer">
          <div className="tw-w-full">
            {(isEnrolled || reserveProgressSpace) && (
              <div className="tw-mb-2 tw-min-h-7 tw-text-xs" aria-hidden={!isEnrolled}>
                {isEnrolled && (
                  <>
                    <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-[var(--text-secondary)]">
                      <span>Course progress</span>
                      {hasProgress && <span className="tw-font-semibold tw-text-[var(--text-primary)]">{isCompleted ? 100 : Math.floor(displayProgress)}%</span>}
                    </div>
                    {hasProgress ? (
                      <div
                        className="tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-slate-200"
                        role="progressbar"
                        aria-label={`${course.title} progress`}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={isCompleted ? 100 : Math.floor(displayProgress)}
                      >
                        <div className={`tw-h-full tw-rounded-full ${isCompleted ? 'tw-bg-emerald-500' : 'tw-bg-blue-600'}`} style={{ width: `${displayProgress}%` }} />
                      </div>
                    ) : (
                      <span className="tw-text-slate-500">{progressLoading ? 'Loading progress...' : 'Progress unavailable'}</span>
                    )}
                  </>
                )}
              </div>
            )}
            {disablePrimaryAction && course.access_scope === 'classroom_only' ? (
              <button type="button" className="tw-mb-4 tw-flex tw-w-full tw-cursor-not-allowed tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-bg-slate-200 tw-p-[0.6rem] tw-font-medium tw-text-slate-500" disabled>
                <Icon icon="fluent:lock-closed-24-regular" /> Access unavailable
              </button>
            ) : isCompleted ? (
              <button
                type="button"
                className={`tw-mb-4 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-bg-emerald-600 tw-font-medium tw-text-white tw-transition-colors hover:tw-bg-emerald-700 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-emerald-600 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${compact ? 'tw-p-[0.35rem_0.65rem] tw-text-[0.8rem]' : 'tw-p-[0.8rem] tw-text-sm'}`}
                onClick={(event) => {
                  event.stopPropagation();
                  openCourse();
                }}
                disabled={isLoading || !onPrimaryAction || disablePrimaryAction}
                aria-label={`Continue ${course.title}`}
              >
                <Icon icon="mdi:play-circle" aria-hidden="true" />
                Continue
              </button>
            ) : (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handlePrimaryAction();
            }}
            className={`[&.free-btn]:tw-bg-[#22c55e] [&.free-btn]:tw-text-[white] [&.free-btn:hover]:tw-bg-[#1ea550] [&.free-btn:hover]:tw-text-[white] [&.paid-btn]:tw-bg-[#4361ee] [&.paid-btn]:tw-text-[white] [&.paid-btn:hover]:tw-bg-[#2541df] [&.paid-btn:hover]:tw-text-[white] [&_.btn-icon]:tw-text-[1.25rem] [&_.btn-icon]:!tw-text-[white] [&_.btn-icon]:!tw-bg-[transparent] [&_.btn-icon]:!tw-p-0 [&_.btn-icon]:!tw-rounded-[0] [&_.btn-icon]:tw-flex [&_.btn-icon]:tw-items-center [&_.btn-icon]:tw-justify-center [&_.btn-icon_svg]:!tw-bg-[transparent] [&_.btn-icon_svg]:![fill:white] [&_.btn-icon_svg]:!tw-rounded-[0] [&_.btn-icon_svg]:!tw-p-0 [&:hover_.btn-icon]:!tw-text-[white] [&:hover_.btn-icon]:![fill:white] [&:hover_.btn-icon]:!tw-bg-[transparent] [&:hover_.btn-icon_svg]:!tw-text-[white] [&:hover_.btn-icon_svg]:![fill:white] [&:hover_.btn-icon_svg]:!tw-bg-[transparent] tw-cursor-pointer tw-text-center tw-whitespace-nowrap tw-min-w-[auto] tw-w-full tw-mb-4 [&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] watch-now-btn ui-button ui-button-primary   ${compact ? "tw-p-[0.35rem_0.65rem] tw-text-[0.8rem] tw-gap-1 tw-min-w-[0] [&_.btn-icon]:tw-text-[1rem] watch-now-btn--compact" : ""}`}
            disabled={isLoading || !onPrimaryAction || disablePrimaryAction}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <div
                  className="tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-text-slate-50"
                  role="status"
                  style={{ width: '16px', height: '16px' }}
                >
                  <span className="tw-sr-only">Loading...</span>
                </div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                {showPrice && (
                  <span className="tw-font-bold tw-pr-[0.65rem] tw-border-r-[1px] tw-border-solid tw-border-r-[rgba(255,_255,_255,_0.4)] watch-now-price">
                    {isFree ? 'Free' : <>{getCurrencySymbol(course.currency)}{course.price ?? '0.00'}</>}
                  </span>
                )}
                <span className="tw-inline-flex tw-items-center tw-justify-center tw-gap-[0.35rem] watch-now-action">
                  <Icon
                    icon={isContinueAction || isFree ? 'mdi:play-circle' : 'mdi:lock'}
                    className={"[[data-theme='dark']_&:hover]:tw-bg-[var(--bg-tertiary)] tw-text-[20px] btn-icon"}
                  />
                  {computedPrimaryLabel}
                </span>
              </>
            )}
          </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseCatalogCard;
